import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import * as SolicitationTitle from './columns/SolicitationTitle';
import * as SolicitationType from './columns/SolicitationType';
import * as SolicitationCategory from './columns/SolicitationCategory';
import * as SolicitationLocation from './columns/SolicitationLocation';
import * as SolicitationDateDue from './columns/SolicitationDateDue';
import * as SolicitationStatus from './columns/SolicitationStatus';
import * as DetailsLink from './columns/DetailsLink';
import { Color } from '../../../../styles/Color';
import { Css, ITheme, Responsive, RowRole } from '../../../../ui';
import { ISolicitationEntry } from '../../../../contentful/types/ISolicitationEntry';

interface ISolicitationListRowProps {
	solicitation: ISolicitationEntry;
	isFirst?: boolean;
}

const StyledRow = styled(RowRole, {
	shouldForwardProp: (props) => isPropValid(props),
})<{ isFirst?: boolean }, ITheme>`
	display: flex;
	align-items: stretch;
	justify-content: space-between;
	padding: ${Css.pxToRem(16)} 0 ${Css.pxToRem(18)};
	border-bottom: 1px solid ${Color.Neutral2};
	height: fit-content;

	${({ isFirst }) => isFirst && `padding-top: ${Css.pxToRem(24)};`}

	:last-of-type {
		border-bottom: none;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
		padding: ${Css.pxToRem(16)} 0;
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		grid-template-areas:
			'title type status'
			'title location status'
			'title category status'
			'title dueDate details';
		grid-gap: ${Css.pxToRem(8)};
		grid-row-gap: ${Css.pxToRem(2)};

		// Apply border to elements in the second column (type, location, category)
		${({ theme }) => `
		  > div:nth-child(2),
		  > div:nth-child(4),
		  > div:nth-child(3) {
			border-bottom: 1px solid ${theme.color.primaryLight};
		  }
		`}
	}
`;

export function SolicitationListRow({
	solicitation,
	isFirst,
}: ISolicitationListRowProps) {
	const { title, type, category, location, proposalDue, status, slug } =
		solicitation.fields;

	return (
		<StyledRow isFirst={isFirst}>
			<SolicitationTitle.Cell title={title} />

			<SolicitationType.Cell type={type} />

			<SolicitationCategory.Cell category={category} />

			<SolicitationLocation.Cell location={location} />

			<SolicitationDateDue.Cell proposalDue={proposalDue} />

			<SolicitationStatus.Cell status={status} />

			<DetailsLink.Cell slug={slug} solicitationTitle={title} />
		</StyledRow>
	);
}
